import { useDispatch, useSelector } from "react-redux";

import { LookupIcon, ProfilingIcon } from "@/assets/icons";
import TabSelect from "@/components/tabSelect";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { useValidationAnalysisConfigInput } from "@/hooks/spatialAnalysis/useValidationAnalysisConfig";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { setSpatialConfig } from "@/store/slice/spatialSlice";
import {
  EOutputAnalysisType,
  EOutputGridType,
  OutputGridType
} from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import { H3CustomOption } from "./data";
import GridOutput from "./gridOutput";
import ProfilingOutput from "./profilingOutput";

const GEOHASH_AREA_SIZE = {
  1: 25000000000000, // 5,000km x 5,000km
  2: 781250000000, // 1,250km x 625km
  3: 24336000000, // 156km x 156km
  4: 762450000, // 39.1km x 19.5km
  5: 23912100, // 4.89km x 4.89km
  6: 744200 // 1.22km x 0.61km
};
const H3_AREA_SIZE = {
  1: 609788440000, // 609,788.44 Km2
  2: 86801780000, // 86,801.78 Km2
  3: 12393430000, // 12,393.43Km2
  4: 1770340000, // 1,770.34Km2
  5: 252900000, // 252.90Km2
  6: 36120000, // 36.12Km2
  7: 5160000, // 5.16Km2
  8: 730000, // 0.73Km2
  9: 100000, // 0.10Km2
  10: 10000 // 0.01Km2
};

const OUTPUT_VISUALIZATION = [
  {
    value: EOutputAnalysisType.GRID,
    label: "Grid",
    icon: <LookupIcon />
  },
  {
    value: EOutputAnalysisType.PROFILING,
    label: "Profiling",
    icon: <ProfilingIcon />
  }
];

export const renderH3Option = (option: H3CustomOption) => {
  return (
    <div className="flex justify-between">
      <span
        className={cn(
          "text-xs text-txtHeading w-1/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.value}
      </span>
      <span
        className={cn(
          "text-xs text-txtHeading w-1/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.radius}
      </span>
      <span
        className={cn(
          "text-xs text-txtHeading text-right w-2/4",
          option.disabled && "text-gray-300"
        )}
      >
        {option.label}
      </span>
    </div>
  );
};

const SpatialOutputAnalysis = ({
  isOutputValidationError = false,
  isResolutionValidationError = false
}: {
  isOutputValidationError?: boolean;
  isResolutionValidationError?: boolean;
}) => {
  const dispatch = useDispatch();
  const { spatialConfig } = useSelector((state: RootState) => state.spatial);
  const { validationErrors } = useValidationAnalysisConfigInput();

  const handleChangeGridType = (value: OutputGridType) =>
    dispatch(setSpatialConfig({ ...spatialConfig, gridType: value }));

  const handleChangeGridResolution = (value: IOptionItem | null) =>
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        gridResolution: value
      })
    );

  const handleChangeH3Resolution = (value: H3CustomOption | null) =>
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        h3Resolution: value
      })
    );

  const renderGeohashOption = (option: IOptionItem) => (
    <span
      className={cn(
        "text-xs text-txtHeading",
        option.disabled && "text-gray-300"
      )}
    >
      {option.label}
    </span>
  );

  const GEOHASH_RESOLUTION_OPTION: IOptionItem[] = [
    {
      label: "1 ≤ 5,000km x 5,000km", // Original: 5,000km x 5,000km
      disabled: spatialConfig.areaSize / GEOHASH_AREA_SIZE[1] >= 1000,
      value: "1"
    },
    {
      label: "2 ≤ 1,250km x 625km", // Original: 1,250km x 625km
      disabled: spatialConfig.areaSize / GEOHASH_AREA_SIZE[2] >= 1000,
      value: "2"
    },
    {
      label: "3 ≤ 156km x 156km", // Original: 156km x 156km
      disabled: spatialConfig.areaSize / GEOHASH_AREA_SIZE[3] >= 1000,
      value: "3"
    },
    {
      label: "4 ≤ 39.1km x 19.5km", // Original: 39.1km x 19.5km
      disabled: spatialConfig.areaSize / GEOHASH_AREA_SIZE[4] >= 1000,
      value: "4"
    },
    {
      label: "5 ≤ 4.89km x 4.89km", // Original: 4.89km x 4.89km
      disabled: spatialConfig.areaSize / GEOHASH_AREA_SIZE[5] >= 1000,
      value: "5"
    },
    {
      label: "6 ≤ 1.22km x 0.61km", // Original: 1.22km x 0.61km
      disabled: spatialConfig.areaSize / GEOHASH_AREA_SIZE[6] >= 1000,
      value: "6"
    }
  ];

  const H3_RESOLUTION: H3CustomOption[] = [
    {
      label: "609,788.44 Km2",
      radius: "483.05 Km",
      value: "1",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[1] >= 1000
    },
    {
      label: "86,801.78 Km2",
      radius: "182.51Km",
      value: "2",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[2] >= 1000
    },
    {
      label: "12,393.43Km2",
      radius: "68.97Km",
      value: "3",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[3] >= 1000
    },
    {
      label: "1,770.34Km2",
      radius: "26.07Km",
      value: "4",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[4] >= 1000
    },
    {
      label: "252.90Km2",
      radius: "9.85Km",
      value: "5",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[5] >= 1000
    },
    {
      label: "36.12Km2",
      radius: "3.72Km",
      value: "6",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[6] >= 1000
    },
    {
      label: "5.16Km2",
      radius: "1.40Km",
      value: "7",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[7] >= 1000
    },
    {
      label: "0.73Km2",
      radius: "0.53Km",
      value: "8",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[8] >= 1000
    },
    {
      label: "0.10Km2",
      radius: "0.20Km",
      value: "9",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[9] >= 1000
    },
    {
      label: "0.01Km2",
      radius: "0.07Km",
      value: "10",
      disabled: spatialConfig.areaSize / H3_AREA_SIZE[10] >= 1000
    }
  ];

  return (
    <div className="flex flex-col px-4 gap-1 mt-4">
      <span className="font-sans font-semibold text-txtHeading text-sm">
        Output Analysis
      </span>
      <p className="text-txtBody font-sans text-[10px]">
        Select your preferred output visualization.
      </p>
      <div
        className={twMerge(
          isOutputValidationError &&
            "border border-cinnabar rounded-xl px-2 pt-1"
        )}
      >
        <div className="flex h-[38px] bg-whisper rounded-md items-center px-1 mt-1">
          <TabSelect
            tabList={OUTPUT_VISUALIZATION}
            selected={spatialConfig?.outputType}
            className="bg-transparent w-full mt-0 px-0"
            buttonClassName="flex"
            buttonSelectedClassName="!bg-white !border-teal-500 font-bold"
            wrapperListClassName="w-full justify-between gap-1"
            onSelect={(value) => {
              dispatch(
                setSpatialConfig({
                  ...spatialConfig,
                  outputType: value as EOutputAnalysisType
                })
              );
            }}
          />
        </div>
        <div className="flex flex-col my-2 gap-3">
          {spatialConfig?.outputType === EOutputAnalysisType.GRID && (
            <GridOutput
              selected={spatialConfig.gridType}
              onChange={handleChangeGridType}
            />
          )}
          {spatialConfig?.outputType === EOutputAnalysisType.PROFILING && (
            <ProfilingOutput />
          )}
        </div>
      </div>
      {isOutputValidationError && (
        <p className="px-4 text-[10px] text-cinnabar self-start my-1">
          {validationErrors.outputAnalysisMessage}
        </p>
      )}
      {/* Resolution settings */}
      <div
        className={twMerge(
          "flex-col",
          spatialConfig.outputType === EOutputAnalysisType.GRID
            ? "flex"
            : "hidden"
        )}
      >
        <span className="text-txtHeading font-bold font-sans text-sm">
          Resolution Settings
        </span>
        {spatialConfig?.gridType === EOutputGridType.GEOHASH && (
          <Dropdown
            isValidationError={isResolutionValidationError}
            className="mt-3"
            placeholder="Resolution"
            value={spatialConfig.gridResolution}
            data={GEOHASH_RESOLUTION_OPTION}
            renderOption={renderGeohashOption}
            onSelect={handleChangeGridResolution}
          />
        )}
        {spatialConfig?.gridType === EOutputGridType.H3 && (
          <Dropdown
            isValidationError={isResolutionValidationError}
            className="mt-3"
            placeholder="Resolution"
            value={spatialConfig.h3Resolution}
            data={H3_RESOLUTION}
            onSelect={handleChangeH3Resolution}
            renderOption={renderH3Option}
            renderSelectValue={renderH3Option}
          />
        )}
        {isResolutionValidationError && (
          <p className="px-2 text-[10px] text-cinnabar self-start mt-1">
            {validationErrors.resolutionMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default SpatialOutputAnalysis;
